import React, { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLoaderData, Outlet } from 'react-router-dom';

import Loading from '@components/Loading/Loading';
import { IRootState } from '@store/reducers';
import { setPermissionList } from '@store/reducers/permissionSlice';
import { logOut, updateUserInfo } from '@store/reducers/userSlice';
import throttle from 'lodash/throttle';

import type { AppDispatch } from '@store/index';

const timeout = 2 * 60 * 60 * 1000; // 2 hours
const throttlePeriod = 5 * 1000; // 5 seconds

function InitLayout() {
  const initData: any = useLoaderData();
  const [isInit, setIsInit] = useState(false);
  const isLoggedIn = useSelector(
    (state: IRootState) => !!state.userSlice.accessToken,
  );
  const dispatch = useDispatch<AppDispatch>();

  const handleLogoutAndPrompt = useCallback(() => {
    localStorage.setItem('shortTermAutoLogout', 'true');
    dispatch(logOut());
  }, [dispatch]);

  useEffect(() => {
    // Early return if not logged in; no need to set up listeners or timeouts.
    if (!isLoggedIn) return undefined;

    let timeoutId: ReturnType<typeof setTimeout> = setTimeout(
      handleLogoutAndPrompt,
      timeout,
    );

    // 使用 Lodash throttle 來限制重置事件觸發的頻率
    const handleActivity = throttle(() => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(handleLogoutAndPrompt, timeout);
    }, throttlePeriod);

    // 聆聽所有使用者互動事件
    const events = ['mousemove', 'mousedown', 'keydown', 'focus', 'scroll'];
    events.forEach((event) =>
      document.addEventListener(event, handleActivity, true),
    );

    // Effect 清理
    return () => {
      clearTimeout(timeoutId);

      events.forEach((event) =>
        document.removeEventListener(event, handleActivity, true),
      );
      handleActivity.cancel(); // Cancel any pending execution of the throttle function
    };
  }, [isLoggedIn, handleLogoutAndPrompt]);

  useEffect(() => {
    if (initData && !isInit) {
      dispatch(setPermissionList(initData.permissionList));
      dispatch(
        updateUserInfo({
          accessToken: initData.accessToken,
          sellerId: initData.sellerId,
          userId: initData.userId,
          name: initData.name,
          type: initData.type,
          account: initData.account,
          userName: initData.userName,
        }),
      );
    }

    setIsInit(true);
  }, []);

  if (!isInit) {
    return <Loading style={{ position: 'fixed' }} spinBGColor="#fff" />;
  }
  return <Outlet />;
}

export default InitLayout;
